import React, { Component } from 'react';

import Button from '../base/Button';
import Modal from '../base/Modal';
import ModalContent from '../base/ModalContent';
import ModalTitle from '../base/ModalTitle';
import documentViewer from '../../services/documentViewer';
import getStyles from './styles/DocumentViewerModal.styles';

const WEBVIEWER_LICENSE =
    'Hexagon Manufacturing Intelligence, Inc.:OEM:a5jOK000001ObfN::B+3:AMS(20270315):416A6917267C7EFF51B7F1DB2B51410996A8D2C881FF094EE33778F28100692B0F8CCF75656D';

const WEBVIEWER_PATH = '/document-viewer/WebViewer/lib';

const SHIM_KEY = 'documentViewerBlobShim';
const IMAGE_TYPES = /^image\//i;

/* eslint-disable no-param-reassign */
function patchBlobImageWindowAsync(win) {
    if (!win || !win.URL) return;
    if (win[SHIM_KEY]) return;
    const origCreate = win.URL.createObjectURL;
    if (typeof origCreate !== 'function') return;

    const pendingImages = new Map();

    win.URL.createObjectURL = function createObjectURL(obj) {
        const url = origCreate.call(win.URL, obj);
        if (obj && typeof obj.type === 'string' && IMAGE_TYPES.test(obj.type)) {
            pendingImages.set(url, obj);
        }
        return url;
    };

    if (win.HTMLImageElement) {
        const imgProto = win.HTMLImageElement.prototype;
        const origSetAttr = imgProto.setAttribute;
        const srcDesc = Object.getOwnPropertyDescriptor(imgProto, 'src')
            || Object.getOwnPropertyDescriptor(Object.getPrototypeOf(imgProto), 'src');

        if (srcDesc && typeof srcDesc.set === 'function') {
            const convertAndSet = (img, blobUrl) => {
                const blob = pendingImages.get(blobUrl);
                if (blob) {
                    const reader = new win.FileReader();
                    reader.onload = () => { srcDesc.set.call(img, reader.result); };
                    reader.onerror = () => { srcDesc.set.call(img, blobUrl); };
                    reader.readAsDataURL(blob);
                    return true;
                }
                fetch(blobUrl)
                    .then((res) => res.blob())
                    .then((fetchedBlob) => {
                        const reader = new win.FileReader();
                        reader.onload = () => { srcDesc.set.call(img, reader.result); };
                        reader.onerror = () => { srcDesc.set.call(img, blobUrl); };
                        reader.readAsDataURL(fetchedBlob);
                    })
                    .catch(() => { srcDesc.set.call(img, blobUrl); });
                return true;
            };

            Object.defineProperty(imgProto, 'src', {
                configurable: true,
                enumerable: srcDesc.enumerable,
                get: srcDesc.get,
                set(value) {
                    const s = value == null ? '' : String(value);
                    if (s.indexOf('blob:') === 0 && convertAndSet(this, s)) return;
                    srcDesc.set.call(this, value);
                },
            });

            imgProto.setAttribute = function setAttribute(name, value) {
                if (String(name).toLowerCase() === 'src') {
                    const s = value == null ? '' : String(value);
                    if (s.indexOf('blob:') === 0 && convertAndSet(this, s)) {
                        return undefined;
                    }
                }
                return origSetAttr.call(this, name, value);
            };

            win[SHIM_KEY] = { origCreate, origSetAttr, srcDesc, imgProto, pendingImages };
        }
        else {
            win[SHIM_KEY] = { origCreate, pendingImages };
        }
    }
    else {
        win[SHIM_KEY] = { origCreate, pendingImages };
    }
}
/* eslint-enable no-param-reassign */

function patchAllFrames(root) {
    if (!root) return;
    const frames = root.querySelectorAll ? root.querySelectorAll('iframe') : [];
    Array.prototype.forEach.call(frames, (f) => {
        try {
            patchBlobImageWindowAsync(f.contentWindow);
        }
        catch (e) {
            // cross-origin frames cannot be patched
        }
    });
}

function installBlobImageShim(rootEl) {
    patchBlobImageWindowAsync(window);
    patchAllFrames(rootEl || document);
    const obs = rootEl && window.MutationObserver
        ? new MutationObserver(() => patchAllFrames(rootEl))
        : null;
    if (obs) {
        obs.observe(rootEl, { childList: true, subtree: true });
    }
    return () => {
        if (obs) {
            obs.disconnect();
        }
    };
}

if (!document.getElementById('docviewer-spin-style')) {
    const styleEl = document.createElement('style');
    styleEl.id = 'docviewer-spin-style';
    styleEl.textContent = '@keyframes docviewer-spin { 100% { transform: rotate(360deg); } }';
    document.head.appendChild(styleEl);
}

const THREE_D_EXTENSIONS = new Set([
    'glb',
    'gltf',
    'stl',
]);

const VIDEO_EXTENSIONS = new Set([
    'mp4',
    'webm',
    'ogg',
    'mpeg',
]);

const AUDIO_EXTENSIONS = new Set([
    'mp3',
    'wav',
    'oga',
]);

let webViewerScriptPromise;
let threeJsModules;

const loadWebViewerScript = () => {
    if (window.WebViewer) return Promise.resolve(window.WebViewer);
    if (webViewerScriptPromise) return webViewerScriptPromise;

    webViewerScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${WEBVIEWER_PATH}/webviewer.min.js`;
        script.async = true;
        script.onload = () => resolve(window.WebViewer);
        script.onerror = () => reject(new Error('Unable to load the document viewer runtime.'));
        document.head.appendChild(script);
    });
    return webViewerScriptPromise;
};

const loadThreeJs = () => {
    if (threeJsModules) return Promise.resolve(threeJsModules);
    const CDN = 'https://esm.sh/three@0.170.0';
    return Promise.all([
        import(/* webpackIgnore: true */ CDN),
        import(/* webpackIgnore: true */ `${CDN}/addons/controls/OrbitControls.js`),
        import(/* webpackIgnore: true */ `${CDN}/addons/loaders/GLTFLoader.js`),
        import(/* webpackIgnore: true */ `${CDN}/addons/loaders/STLLoader.js`),
    ]).then(([
        THREE,
        OrbitMod,
        GLTFMod,
        STLMod,
    ]) => {
        threeJsModules = {
            THREE,
            OrbitControls: OrbitMod.OrbitControls,
            GLTFLoader: GLTFMod.GLTFLoader,
            STLLoader: STLMod.STLLoader,
        };
        return threeJsModules;
    });
};

class DocumentViewerModal extends Component {
    constructor(props) {
        super(props);
        this.viewerElement = React.createRef();
        this.mediaElement = React.createRef();
        this.viewerInstance = null;
        this.threeCleanup = null;
        this.uninstallBlobShim = null;
        this.unsubscribe = null;
        this.state = {
            isOpen: false,
            isLoading: false,
            isReady: false,
            isSaving: false,
            model: null,
            status: '',
            error: '',
        };
    }

    render() {
        const {
            isOpen,
            isLoading,
            isReady,
            isSaving,
            model,
            status,
            error,
        } = this.state;
        const styles = getStyles();
        let statusStyle = styles.status;
        if (error) statusStyle = { ...statusStyle, ...styles.errorStatus };
        else if (status === 'Saved.' || status === 'Downloaded.') {
            statusStyle = { ...statusStyle, ...styles.successStatus };
        }

        return (
            <Modal
                contextStyles={ {
                    modal: styles.modal,
                } }
                isModalOpen={ isOpen }
                onClose={ this.handleClose }
                testID="document-viewer">
                <ModalTitle
                    contextStyles={ styles.title }
                    disableTypography>
                    <div style={ styles.titleRow }>
                        <div style={ styles.titleActions }>
                            { model && model.editable && (
                                <Button
                                    disabled={ !isReady || isSaving }
                                    onClick={ this.handleSave }
                                    text={ isSaving ? 'Saving...' : 'Save PDF' } />
                            ) }
                            <Button
                                onClick={ this.handleClose }
                                text="Close" />
                        </div>
                        <span style={ styles.titleText }>
                            { model ? model.fileName : 'Document Viewer' }
                        </span>
                        <span style={ statusStyle }>
                            { error || status }
                        </span>
                    </div>
                </ModalTitle>
                <ModalContent contextStyles={ styles.content }>
                    { (isLoading || isSaving) && (
                        <div style={ styles.loaderOverlay }>
                            <div style={ styles.spinner } />
                        </div>
                    ) }
                    { this.renderViewer(styles) }
                </ModalContent>
            </Modal>
        );
    }

    componentDidMount() {
        this.unsubscribe = documentViewer.subscribe(this.handleOpen);
        loadWebViewerScript().catch(() => {});
    }

    componentWillUnmount() {
        if (this.unsubscribe) this.unsubscribe();
        this.disposeViewer();
    }

    renderViewer = (styles) => {
        const {
            model,
            error,
        } = this.state;

        if (!model) {
            return (
                <div style={ styles.message }>
                    { error || 'Document is unavailable.' }
                </div>
            );
        }
        if (model.extension === 'pdf') {
            return (
                <div
                    ref={ this.viewerElement }
                    style={ styles.viewer } />
            );
        }
        if (THREE_D_EXTENSIONS.has(model.extension)) {
            return (
                <div
                    ref={ this.viewerElement }
                    style={ styles.viewer } />
            );
        }
        if (VIDEO_EXTENSIONS.has(model.extension)) {
            return (
                <div style={ styles.mediaContainer }>
                    { /* eslint-disable-next-line jsx-a11y/media-has-caption */ }
                    <video
                        controls
                        playsInline
                        preload="auto"
                        ref={ this.mediaElement }
                        style={ styles.video }
                        onLoadedData={ this.handleMediaLoaded }>
                        <source
                            src={ model.contentUrl }
                            type="video/mp4" />
                        <track kind="captions" />
                    </video>
                </div>
            );
        }
        if (AUDIO_EXTENSIONS.has(model.extension)) {
            return (
                <div style={ styles.mediaContainer }>
                    { /* eslint-disable-next-line jsx-a11y/media-has-caption */ }
                    <audio
                        controls
                        preload="auto"
                        ref={ this.mediaElement }
                        style={ styles.audio }
                        onLoadedData={ this.handleMediaLoaded }>
                        <source
                            src={ model.contentUrl }
                            type="audio/mpeg" />
                        <track kind="captions" />
                    </audio>
                </div>
            );
        }
        if (/^image\//i.test(model.contentType)) {
            return (
                <img
                    alt={ model.fileName }
                    src={ model.contentUrl }
                    style={ styles.image } />
            );
        }
        return (
            <div style={ styles.message }>
                { `Unsupported file type: ${model.fileName}` }
            </div>
        );
    };

    handleMediaLoaded = () => {
        this.setState({ isReady: true, isLoading: false });
    };

    handleClose = () => {
        documentViewer.close();
    };

    handleSave = async() => {
        const {
            model,
            isReady,
            isSaving,
        } = this.state;
        if (!model || !model.editable || !isReady || isSaving || !this.viewerInstance) return;

        this.setState({
            isSaving: true,
            status: '',
            error: '',
        });

        try {
            const { Core } = this.viewerInstance;
            const {
                documentViewer: coreDocumentViewer,
                annotationManager,
            } = Core;
            const tool = coreDocumentViewer.getToolMode();
            if (tool && typeof tool.complete === 'function') {
                tool.complete();
            }
            annotationManager.deselectAllAnnotations();
            await new Promise((resolve) => setTimeout(resolve, 100));

            const xfdfString = await annotationManager.exportAnnotations();
            const fileData = await coreDocumentViewer.getDocument().getFileData({
                xfdfString,
            });
            const pdfBlob = new Blob([ new Uint8Array(fileData) ], {
                type: 'application/pdf',
            });
            const response = await fetch(model.replaceUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/pdf',
                },
                body: pdfBlob,
            });
            if (!response.ok) {
                throw new Error(`Save failed (${response.status}).`);
            }

            this.setState({
                isSaving: false,
                isLoading: true,
                status: '',
            });
            const separator = model.contentUrl.includes('?') ? '&' : '?';
            fetch(`${model.contentUrl}${separator}_ts=${Date.now()}`)
                .then((res) => res.arrayBuffer())
                .then((buf) => {
                    this.viewerInstance.UI.loadDocument(
                        new Uint8Array(buf.slice(0)),
                        { extension: 'pdf', filename: model.fileName }
                    );
                });
        }
        catch (error) {
            this.setState({
                isSaving: false,
                status: '',
                error: error.message,
            });
        }
    };

    disposeViewer = () => {
        if (this.threeCleanup) {
            this.threeCleanup();
            this.threeCleanup = null;
        }
        if (this.uninstallBlobShim) {
            this.uninstallBlobShim();
            this.uninstallBlobShim = null;
        }
        if (this.viewerInstance && typeof this.viewerInstance.dispose === 'function') {
            this.viewerInstance.dispose();
        }
        this.viewerInstance = null;
        if (this.viewerElement.current) {
            this.viewerElement.current.innerHTML = '';
        }
    };

    initializePdfViewer = (model) => {
        const container = this.viewerElement.current;
        if (!container) {
            this.setState({ error: 'Viewer container not ready. Please try again.' });
            return;
        }
        this.uninstallBlobShim = installBlobImageShim(container);

        const pdfBytesPromise = fetch(model.contentUrl)
            .then((res) => {
                if (!res.ok) throw new Error(`Failed to load PDF (${res.status}).`);
                return res.arrayBuffer();
            });
        pdfBytesPromise.catch(() => {}); // prevent unhandled rejection if WebViewer init outlasts the fetch

        loadWebViewerScript()
            .then((WebViewer) => WebViewer({
                disableObjectURLBlobs: true,
                disableStreaming: true,
                enableMobileMode: true,
                enableOptimizedWorkers: false,
                extension: 'pdf',
                licenseKey: WEBVIEWER_LICENSE,
                path: WEBVIEWER_PATH,
                streaming: false,
            }, container))
            .then((instance) => {
                this.viewerInstance = instance;
                patchAllFrames(container);
                const { UI, Core } = instance;
                const {
                    documentViewer: coreDocumentViewer,
                    annotationManager,
                } = Core;

                UI.disableElements([
                    'downloadButton',
                    'printButton',
                    'saveAsButton',
                ]);

                if (!model.editable) {
                    UI.disableElements([
                        'toolbarGroup-Annotate',
                        'toolbarGroup-Shapes',
                        'toolbarGroup-Insert',
                        'toolbarGroup-Edit',
                        'toolbarGroup-FillAndSign',
                        'toolbarGroup-Forms',
                    ]);
                    UI.setToolbarGroup('toolbarGroup-View');
                }

                annotationManager.addEventListener('annotationChanged',
                    (annotations, action, info = {}) => {
                        if (!info.imported) {
                            this.setState({ status: '' });
                        }
                    });

                coreDocumentViewer.addEventListener('documentLoaded', () => {
                    patchAllFrames(container);
                    UI.setFitMode(UI.FitMode.FitWidth);
                    this.setState({
                        isLoading: false,
                        isReady: true,
                        error: '',
                    });
                });

                return pdfBytesPromise;
            })
            .then((arrayBuffer) => {
                const bytes = new Uint8Array(arrayBuffer.slice(0));
                this.viewerInstance.UI.loadDocument(bytes, {
                    extension: 'pdf',
                    filename: model.fileName,
                });
            })
            .catch((error) => {
                this.setState({
                    isReady: false,
                    error: error.message,
                });
            });
    };

    initialize3DViewer = (model) => {
        const modelBytesPromise = fetch(model.contentUrl)
            .then((res) => {
                if (!res.ok) throw new Error(`Failed to load 3D model (${res.status}).`);
                return res.arrayBuffer();
            });

        loadThreeJs()
            .then(({ THREE, OrbitControls, GLTFLoader, STLLoader }) => {
                const container = this.viewerElement.current;
                if (!container) return;

                const width = container.clientWidth;
                const height = container.clientHeight;

                const renderer = new THREE.WebGLRenderer({ antialias: true });
                renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                renderer.setSize(width, height);
                renderer.outputColorSpace = THREE.SRGBColorSpace;
                renderer.toneMapping = THREE.ACESFilmicToneMapping;
                renderer.toneMappingExposure = 1.0;
                container.appendChild(renderer.domElement);

                const scene = new THREE.Scene();
                scene.background = new THREE.Color(0x1a1a2e);

                const camera = new THREE.PerspectiveCamera(50, width / height, 0.01, 10000);

                const controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.08;
                controls.enablePan = true;

                scene.add(new THREE.AmbientLight(0xffffff, 0.6));
                const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
                dirLight.position.set(5, 10, 7);
                scene.add(dirLight);
                const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
                fillLight.position.set(-5, -3, -5);
                scene.add(fillLight);

                const fitCamera = (object) => {
                    const box = new THREE.Box3().setFromObject(object);
                    const size = box.getSize(new THREE.Vector3());
                    const center = box.getCenter(new THREE.Vector3());
                    const maxDim = Math.max(size.x, size.y, size.z);
                    const fov = camera.fov * (Math.PI / 180);
                    const dist = (maxDim / 2) / Math.tan(fov / 2) * 1.5;
                    camera.position.set(
                        center.x + dist * 0.5,
                        center.y + dist * 0.5,
                        center.z + dist
                    );
                    camera.near = dist * 0.001;
                    camera.far = dist * 100;
                    camera.updateProjectionMatrix();
                    controls.target.copy(center);
                    controls.update();
                };

                const onLoaded = (object) => {
                    scene.add(object);
                    fitCamera(object);
                    this.setState({ isLoading: false, isReady: true, error: '' });
                };

                const onError = (err) => {
                    this.setState({
                        isLoading: false,
                        isReady: false,
                        error: err && err.message ? err.message : 'Failed to load 3D model.',
                    });
                };

                modelBytesPromise
                    .then((arrayBuffer) => {
                        if (model.extension === 'stl') {
                            const geometry = new STLLoader().parse(arrayBuffer);
                            geometry.computeVertexNormals();
                            const mat = new THREE.MeshStandardMaterial({
                                color: 0x8899aa, metalness: 0.3, roughness: 0.6,
                            });
                            onLoaded(new THREE.Mesh(geometry, mat));
                        }
                        else {
                            new GLTFLoader().parse(arrayBuffer, '', (gltf) => {
                                onLoaded(gltf.scene);
                            }, onError);
                        }
                    })
                    .catch(onError);

                let animId;
                const animate = () => {
                    animId = requestAnimationFrame(animate);
                    controls.update();
                    renderer.render(scene, camera);
                };
                animate();

                const onResize = () => {
                    const w = container.clientWidth;
                    const h = container.clientHeight;
                    camera.aspect = w / h;
                    camera.updateProjectionMatrix();
                    renderer.setSize(w, h);
                };
                window.addEventListener('resize', onResize);

                this.threeCleanup = () => {
                    window.removeEventListener('resize', onResize);
                    cancelAnimationFrame(animId);
                    controls.dispose();
                    renderer.dispose();
                };
            })
            .catch((error) => {
                this.setState({ isReady: false, error: error.message });
            });
    };

    handleOpen = (viewerData) => {
        if (!viewerData) {
            this.disposeViewer();
            this.setState({
                isOpen: false,
                isLoading: false,
                isReady: false,
                isSaving: false,
                model: null,
                status: '',
                error: '',
            });
            return;
        }

        this.disposeViewer();
        const needsInit = viewerData.extension === 'pdf'
            || THREE_D_EXTENSIONS.has(viewerData.extension);
        this.setState({
            isOpen: true,
            isLoading: needsInit,
            isReady: false,
            isSaving: false,
            model: viewerData,
            status: '',
            error: '',
        }, () => {
            const initViewer = () => {
                if (!this.viewerElement.current) {
                    requestAnimationFrame(initViewer);
                    return;
                }
                if (viewerData.extension === 'pdf') {
                    this.initializePdfViewer(viewerData);
                }
                else if (THREE_D_EXTENSIONS.has(viewerData.extension)) {
                    this.initialize3DViewer(viewerData);
                }
            };
            requestAnimationFrame(initViewer);
        });
    };
}

export default DocumentViewerModal;
